import React, { useState, useEffect } from 'react';
import { Card, CardContent, Typography, IconButton, TextField, Box } from '@mui/material';
import Grid from "@mui/material/Grid2";
import Paper from '@mui/material/Paper';
import { experimentalStyled as styled } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import { arrowDown } from 'images';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useCheckRole } from '_utils/role-access';

// Styling for each card
const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: '#fff',
  ...theme.typography.body2,
  padding: theme.spacing(2),
  textAlign: 'center',
  color: theme.palette.text.secondary,
  ...theme.applyStyles('dark', {
    backgroundColor: '#1A2027',
  }),
}));

const FuelWithDrawCardComponent = ({
  label, charge, currentcharge, isChargeApplicable, fuelChargeEffectiveDate, isFuleChargeDtDisplay, onChargeChange, setIsSaveEnabled, setErrorMessage, errorMessage, id
}) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [editableCharge, setEditableCharge] = useState(charge);


  const [editableDate, setEditableDate] = useState(dayjs(fuelChargeEffectiveDate).isValid() ? dayjs(fuelChargeEffectiveDate) : dayjs());


  const today = dayjs();
  const minDate = today.subtract(12, 'month');
  const maxDate = today.add(12, 'month');
  //Check role
  const hasEditFuelChargesAccess = useCheckRole(4, "EditFuelCharges")

  const handleEditClick = () => {
    setIsEditMode(true);
  };

  const handleChargeChange = (e) => {
    const value = e.target.value;
    setEditableCharge(value);
    setIsSaveEnabled(false)
    // Check if the value is a valid number format (digits and optional decimal with up to 4 places)
    const formatRegex = /^\d*\.?\d{0,4}$/;
    const isNumeric = !isNaN(value) && !isNaN(parseFloat(value));
    if (value === '') {
      setEditableCharge('');
      setErrorMessage(prev => ({
        ...prev,
        [id]: 'This field is required'
      }));
      return;
    }
    if (!isNumeric) {
      setErrorMessage(prev => ({
        ...prev,
        [id]: 'Only numeric values and single decimal points allowed'
      }));
      return;
    }

    if (!formatRegex.test(value)) {
      setErrorMessage(prev => ({
        ...prev,
        [id]: 'Value must be between 0.0001% and 100% (with up to 4 decimal places)'
      }));
      return;
    }

    const num = parseFloat(value);

    if (!isNaN(num) && num >= 0.0001 && num <= 100) {
      setEditableCharge(value);
      // setErrorMessage('')
      setErrorMessage(prev => ({
        ...prev,
        [id]: ''
      }));
      if (!errorMessage.injection && !errorMessage.withdraw)
        setIsSaveEnabled(true)
    } else {
      setErrorMessage(prev => ({
        ...prev,
        [id]: 'Value must be between 0.0001% and 100% (with up to 4 decimal places)'
      }));
    }
  };

  const handleDatePickerClose = () => {
    setIsEditMode(false);
  };

  const handleDateChange = (newDate) => {
    setEditableDate(newDate);
    const dateObj = dayjs(newDate).local().startOf('month');
    const isoDateString = dateObj.toISOString();
    if (!errorMessage.injection && !errorMessage.withdraw)
      onChargeChange(label, isoDateString);
  };

  const handleBlur = () => {
    setIsEditMode(false);
    if (onChargeChange && (!errorMessage.injection && !errorMessage.withdraw)) {
      onChargeChange(label, editableCharge);
    }
  };

  useEffect(() => {
    setEditableCharge(charge);
  }, [charge]);

  useEffect(() => {
    if (fuelChargeEffectiveDate) {
      setEditableDate(dayjs(fuelChargeEffectiveDate).isValid() ? dayjs(fuelChargeEffectiveDate) : dayjs());
    }
  }, [fuelChargeEffectiveDate]);

  return (
    <Grid size={{ xs: 12, sm: 12, md: 4 }} className="CardDetail card-layout">
      <Item>
        <Card sx={{ minWidth: 345 }}>
          <CardContent className='card-last-item'>
            <Typography gutterBottom variant="h5" component="div" className="cardcontent card-last-item-content">
              <div className="service-card-heading">
                {label}
                <img
                  src={arrowDown}
                  alt="arrow"
                  className="editicon"
                />
              </div>

              {(isEditMode && !isFuleChargeDtDisplay) ? (
                <TextField
                  value={editableCharge}
                  onChange={handleChargeChange}
                  onBlur={handleBlur}
                  variant="outlined"
                  size="small"
                  type="text"
                  autoFocus
                  fullWidth
                  id={label}
                  sx={{
                    maxWidth: "240px", // or use maxWidth: "300px"
                    "& .MuiFormHelperText-root": {
                      textTransform: "none"
                    }
                  }}
                  error={errorMessage[id]}
                  helperText={errorMessage[id]}
                />
              ) : (
                <>
                  {isChargeApplicable && (
                    <div className="service-card-title">{editableCharge}%</div>
                  )}
                </>
              )}

              {!isChargeApplicable && isEditMode ? (
                <div className='calender-widht'>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      views={["year", "month"]}
                      value={editableDate}
                      onChange={handleDateChange}
                      onClose={handleDatePickerClose}
                      minDate={minDate}
                      maxDate={maxDate}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          onBlur={handleDatePickerClose}
                        />
                      )}
                    />
                  </LocalizationProvider>
                </div>
              ) : (
                isFuleChargeDtDisplay && (
                  <div className="service-card-title calender-widht">{editableDate.format('MM/DD/YYYY')}</div>
                )
              )}

              {(!isFuleChargeDtDisplay) && <div className="service-card-body-text">Current Charge: {currentcharge}%</div>}

              {/* Edit Icon */}
              {hasEditFuelChargesAccess && <IconButton onClick={handleEditClick}>
                <EditIcon fontSize="small" />
              </IconButton>}
            </Typography>
          </CardContent>
        </Card>
      </Item>
    </Grid>
  );
};

export default FuelWithDrawCardComponent;
